import { Box, HStack, Icon, IconButton, Link, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { IoChevronUpOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import { ANIMATE_HEIGHT, ANIMATE_OPACITY } from "../../../constants/animations";
import { SearchInfinitePagination, useSearch } from "../../../contexts/Search";
import SearchInput from "./SearchInput";

/**
 * @typedef {Object} Props
 * @property {() => void} onClose
 * @property {any} value the value of the form control, can be an array or a single value
 * @property {any[]} values the value of the form control, normalized to an array
 * @property {import(".").RenderOption} renderOption
 * @property {import(".").RenderSelectedOption} renderSelectedOption
 * @property {import(".").ToggleValue} toggleValue
 * @property {(params: { value: any }) => void} removeValue
 * @property {() => void} reset
 * @property {(value: any) => any} resolveItemByValue
 * @property {import(".").ResolveItemName} resolveItemName
 * @property {import(".").ResolveItemValue} resolveItemValue
 * @property {import("react").MutableRefObject<any>} inputRef
 * @property {boolean} hydrationDone
 *
 * @param {Props} props
 */
function Internal(props) {
  const {
    onClose,
    value: _value,
    values,
    renderOption,
    renderSelectedOption,
    toggleValue,
    removeValue,
    reset,
    resolveItemByValue,
    resolveItemName,
    resolveItemValue,
    inputRef,
    hydrationDone,
  } = props;

  const intl = useIntl();

  const { response: swrResponse } = useSearch();

  const [items, setItems] = useState(swrResponse?.data?.data?.items || []);

  /** @type {import("react").MutableRefObject<HTMLElement[]>} */
  const optionsRefs = useRef([]);

  useEffect(() => {
    if (Array.isArray(swrResponse?.data?.data)) {
      setItems(swrResponse?.data?.data);
    }
  }, [swrResponse?.data?.data]);

  const toggleFirstOption = useCallback(() => {
    if (items.length > 0) {
      toggleValue({ item: items[0] });
    }
  }, [items, toggleValue]);

  const handleReset = useCallback(() => {
    reset();
    optionsRefs.current?.[0]?.focus?.();
  }, [reset]);

  return (
    <Box pb=".5rem">
      <HStack pl="1rem">
        <SearchInput
          inputRef={inputRef}
          optionsRef={optionsRefs}
          toggleFirstOption={toggleFirstOption}
        />

        <IconButton
          display="block"
          ml="auto"
          onClick={onClose}
          icon={<Icon as={IoChevronUpOutline} />}
          aria-label={intl.formatMessage({ defaultMessage: "Fermer" })}
          variant="ghost"
          colorScheme="darkGray"
        />
      </HStack>

      <AnimatePresence initial={false}>
        {values.length > 1 && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={ANIMATE_HEIGHT}>
            <motion.div variants={ANIMATE_OPACITY}>
              <HStack justifyContent="flex-end" px="1rem">
                <Link
                  onClick={handleReset}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleReset();
                    }
                  }}
                  tabIndex={0}>
                  <FormattedMessage defaultMessage="Tout désélectionner" />
                </Link>
              </HStack>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        display="flex"
        flexDirection="column"
        maxHeight="128px"
        px=".75rem"
        overflow="auto">
        {values.map((itemValue) => {
          const item = resolveItemByValue(itemValue);
          return (
            <Box p=".25rem" key={itemValue}>
              {renderSelectedOption({
                item,
                itemValue,
                resolveItemName,
                removeValue,
                hydrationDone,
                isInPopover: true,
              })}
            </Box>
          );
        })}
      </Box>

      {items.length > 0 && (
        <Box px=".25rem" py=".25rem">
          {items.map?.((item, index) => (
            <Fragment key={resolveItemValue({ item })}>
              {renderOption({
                ref: (el) => {
                  optionsRefs.current[index] = el;
                },
                value: _value,
                item,
                resolveItemName,
                resolveItemValue,
                toggleValue,
                focusPrev: () => {
                  if (index === 0) {
                    inputRef.current?.focus?.();
                    inputRef.current?.select?.();
                  }
                  optionsRefs.current?.[index - 1]?.focus?.();
                },
                focusNext: () => {
                  optionsRefs.current?.[index + 1]?.focus?.();
                },
              })}
            </Fragment>
          ))}
        </Box>
      )}

      {items.length === 0 && (
        <Text px="1rem" mt=".5rem">
          <FormattedMessage defaultMessage="Aucun résultat" />
        </Text>
      )}

      <Box px="1rem">
        <SearchInfinitePagination buttonProps={{ size: "xs" }} />
      </Box>
    </Box>
  );
}

export default Internal;
